import { useEffect, useRef } from 'react'
import Snackbar from '@mui/material/Snackbar'
import Alert, { AlertColor } from '@mui/material/Alert'
import Box from '@mui/material/Box'
import { Toast, ToastColor } from '../../types/toast'
import { useToastState, useToastDispatch } from '../../contexts/toasts/toast-context'
import { GLOBAL_TOAST_DURATION_MS } from '../../config/toasts'

/**
 * Maps the legacy EUI toast `color` to the MUI `Alert` `severity`.
 * The EUI-side vocabulary (`primary`/`danger`) differs from MUI's (`info`/`error`).
 */
export const TOAST_COLOR_TO_SEVERITY: Record<ToastColor, AlertColor> = {
	success: 'success',
	danger: 'error',
	warning: 'warning',
	primary: 'info',
}

type ToastAlertProps = {
	toast: Toast,
	onClose: () => void,
}

function ToastAlert( { toast, onClose }: ToastAlertProps ) {
	const remainingMsRef = useRef( GLOBAL_TOAST_DURATION_MS )
	const startedAtRef = useRef( 0 )
	const timerRef = useRef<ReturnType<typeof setTimeout>>()

	const startTimer = ( duration: number ) => {
		startedAtRef.current = Date.now()
		timerRef.current = setTimeout( onClose, duration )
	}

	useEffect( () => {
		startTimer( remainingMsRef.current )
		return () => clearTimeout( timerRef.current )
	}, [] )

	const pauseTimer = () => {
		clearTimeout( timerRef.current )
		remainingMsRef.current -= Date.now() - startedAtRef.current
	}

	const resumeTimer = () => {
		startTimer( remainingMsRef.current )
	}

	return (
		<Alert
			severity={TOAST_COLOR_TO_SEVERITY[ toast.color ]}
			variant="filled"
			onClose={onClose}
			onMouseEnter={pauseTimer}
			onMouseLeave={resumeTimer}
		>
			{toast.title}
		</Alert>
	)
}

export default function MuiToasts() {
	const toasts = useToastState()
	const dispatch = useToastDispatch()

	const removeToast = ( toast: Toast ) => {
		dispatch( {
			type: 'removeToast',
			payload: toast,
		} )
	}

	if ( toasts.length === 0 ) {
		return null
	}

	return (
		<Snackbar open anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
			<Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, width: '100%' }}>
				{ toasts.map( ( toast ) => (
					<ToastAlert key={toast.id} toast={toast} onClose={() => removeToast( toast )} />
				) ) }
			</Box>
		</Snackbar>
	)
}
